import { useMemo, useState } from "react";
import type { InviteRequestInput } from "@pokersports/shared";
import { submitInviteRequest } from "./api";

const pokerShots = [
  {
    src: "/screenshots/poker-lobby.svg",
    title: "High-clarity game lobby"
  },
  {
    src: "/screenshots/poker-table.svg",
    title: "Live multi-table experience"
  }
];

const aiShots = [
  {
    src: "/screenshots/ai-review-report.svg",
    title: "Street-by-street AI analysis"
  },
  {
    src: "/screenshots/ai-leak-detector.svg",
    title: "Leak detection and training plan"
  }
];

const defaultForm: InviteRequestInput = {
  name: "",
  email: "",
  pokerExperience: "intermediate",
  stakes: "",
  message: ""
};

export function App() {
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [feedback, setFeedback] = useState("");

  const canSubmit = useMemo(
    () => form.name && form.email && form.stakes && form.message.length >= 10,
    [form]
  );

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || loading) return;

    setLoading(true);
    setStatus("idle");
    setFeedback("");
    try {
      const response = await submitInviteRequest(form);
      setStatus("success");
      setFeedback(`Request received. Your application ID is ${response.data.id}.`);
      setForm(defaultForm);
    } catch (error) {
      setStatus("error");
      setFeedback(error instanceof Error ? error.message : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-shell">
      <div className="ambient ambient-1" />
      <div className="ambient ambient-2" />

      <div className="page">
        <header className="hero">
          <nav className="nav">
            <div className="brand-wrap">
              <div className="brand-mark" />
              <div className="brand">PokerSports.net</div>
            </div>
            <div className="nav-actions">
              <span className="pill">Invite-Only Access</span>
              <a href="#apply" className="btn btn-secondary">
                Request Invite
              </a>
              <a href="https://user.pokersports.net/login" className="btn btn-primary">
                User Login
              </a>
            </div>
          </nav>

          <div className="hero-grid">
            <div className="hero-content">
              <p className="badge">Invite Only • Rake-Free • Proprietary AI Hand Review</p>
              <h1>The private online poker room built for serious improvement.</h1>
              <p>
                PokerSports.net is an <strong>invite-only</strong> poker platform with zero rake
                and proprietary AI hand review that explains every major decision from preflop to
                river.
              </p>

              <div className="hero-cta-row">
                <a href="#apply" className="btn btn-primary">
                  Join the Waitlist
                </a>
                <a href="#experience" className="btn btn-secondary">
                  Explore Product
                </a>
              </div>
            </div>

            <aside className="hero-panel">
              <h3>Performance Snapshot</h3>
              <ul>
                <li>
                  <span>Rake model</span>
                  <strong>0% (Rake-Free)</strong>
                </li>
                <li>
                  <span>Access policy</span>
                  <strong>Invite-Only Network</strong>
                </li>
                <li>
                  <span>AI review depth</span>
                  <strong>Street-by-street EV feedback</strong>
                </li>
                <li>
                  <span>Learning output</span>
                  <strong>Leak reports + action plan</strong>
                </li>
              </ul>
            </aside>
          </div>
        </header>

        <main>
          <section className="section trust-strip">
            <div>Private Games</div>
            <div>Strict Invite Policy</div>
            <div>Professional Integrity Controls</div>
            <div>Data-Driven Coaching</div>
          </section>

          <section className="section">
            <h2>Why serious players choose PokerSports.net</h2>
            <div className="grid three">
              <article className="card">
                <h3>Invite-only ecosystem</h3>
                <p>Controlled player pool, stronger game integrity, and quality competition.</p>
              </article>
              <article className="card">
                <h3>100% rake-free model</h3>
                <p>Keep your edge. No hidden rake, no predatory economics.</p>
              </article>
              <article className="card">
                <h3>Proprietary AI coaching</h3>
                <p>
                  Upload any played hand and get EV-aware, street-level feedback with practical
                  improvement actions.
                </p>
              </article>
            </div>
          </section>

          <section className="section" id="experience">
            <h2>Online poker experience previews</h2>
            <p className="muted">Representative product visuals for MVP demonstration.</p>
            <div className="grid two">
              {pokerShots.map((shot) => (
                <figure className="shot" key={shot.src}>
                  <img src={shot.src} alt={shot.title} />
                  <figcaption>{shot.title}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section className="section">
            <h2>AI hand review previews</h2>
            <p className="muted">
              Fabricated showcase images illustrating the proprietary post-hand review experience.
            </p>
            <div className="grid two">
              {aiShots.map((shot) => (
                <figure className="shot" key={shot.src}>
                  <img src={shot.src} alt={shot.title} />
                  <figcaption>{shot.title}</figcaption>
                </figure>
              ))}
            </div>
          </section>

          <section className="section timeline">
            <h2>How the improvement loop works</h2>
            <div className="grid three">
              <article className="card">
                <h3>1. Play in private games</h3>
                <p>Focus on decisions without rake drag and with a curated player pool.</p>
              </article>
              <article className="card">
                <h3>2. Run AI review instantly</h3>
                <p>Receive structured feedback on sizing, frequencies, and exploit windows.</p>
              </article>
              <article className="card">
                <h3>3. Apply and benchmark</h3>
                <p>Track leak reduction and compare EV trend session-over-session.</p>
              </article>
            </div>
          </section>

          <section className="section" id="apply">
            <h2>Request an invite</h2>
            <p>
              Access is currently <strong>invite only</strong>. Submit your profile and our team
              will review your request.
            </p>

            <form className="form" onSubmit={onSubmit}>
              <label>
                Full Name
                <input
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Your name"
                  required
                />
              </label>

              <label>
                Email
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  placeholder="you@example.com"
                  required
                />
              </label>

              <label>
                Experience Level
                <select
                  value={form.pokerExperience}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      pokerExperience: event.target.value as InviteRequestInput["pokerExperience"]
                    }))
                  }
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                  <option value="pro">Pro</option>
                </select>
              </label>

              <label>
                Typical Stakes
                <input
                  value={form.stakes}
                  onChange={(event) => setForm((prev) => ({ ...prev, stakes: event.target.value }))}
                  placeholder="Example: NL50 / NL100"
                  required
                />
              </label>

              <label>
                Why do you want to join?
                <textarea
                  value={form.message}
                  onChange={(event) => setForm((prev) => ({ ...prev, message: event.target.value }))}
                  placeholder="Share your goals, volume, and what you want from AI hand review..."
                  rows={6}
                  required
                />
              </label>

              <button className="btn btn-primary" type="submit" disabled={!canSubmit || loading}>
                {loading ? "Submitting..." : "Submit Invite Request"}
              </button>

              {feedback ? (
                <p className={status === "success" ? "feedback success" : "feedback error"}>
                  {feedback}
                </p>
              ) : null}
            </form>
          </section>

          <footer className="section footer-note">
            <p>PokerSports.net — Invite-only online poker with rake-free economics and AI review.</p>
          </footer>
        </main>
      </div>
    </div>
  );
}

